import useOnlineProductQuery from "@nitro/gatsby-source-harvia-pim/hooks/useOnlineProductQuery"
import { Product } from "@nitro/gatsby-source-harvia-pim/types/plugin"
import React, { useContext } from "react"
import { PageContext } from "../../../context/PageContext"
import useDatoMicrocopy from "../../../hooks/useDatoMicrocopy"
import useSiteMetadata from "../../../hooks/useSiteMetadata"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import ProductComparisonAccordion from "./ProductComparisonAccordion"

//import * as styles from "./ProductComparison.module.scss"

type ProductComparisonProps = {
  product: Product
  otherProducts?: Product[]
  query?: string
}

const ProductComparison = (props: ProductComparisonProps) => {
  const { locale, localePim } = useContext(PageContext)
  const t = useDatoMicrocopy("product", locale)
  const { pim } = useSiteMetadata()
  //console.log('Rendering ProductComparison', props)

  const showDiscontinued = false

  const query =
    props.query &&
    (showDiscontinued
      ? [props.query, "product_life_cycle_status:s5_discontinued"].join()
      : [props.query, "product_life_cycle_status:s3_maintenance", "product_life_cycle_status:s4_rampdown"].join())

  const [products] =
    useOnlineProductQuery(
      query || null,
      {
        language: localePim,
        size: "20", // maksimi määrä verrattavia tuotteita
        sort: "sku,asc",
        includeDisabledProducts: true,
      },
      {
        baseQuery: pim.baseParams.join(","),
      }
    ) || []

  const restProducts =
    props.otherProducts || products?.filter(itm => itm.identifier !== props.product?.identifier) || []

  const comparedProducts = [props.product, ...restProducts]

  return (
    <>
      <LoadingSpinner loading={!!props.query && !products} />
      <ProductComparisonAccordion
        title={t("compareFeatures", "Features")}
        products={comparedProducts}
        keys={[
          //"product_family",
          "user_group",
          "power_output",
          "control_type",
          "secondary_control_type",
          "installation_type",
          "installation_orientation",

          "technical_documentation_languages",
          "color",
          "main_material",
          "water_quality_requirement",
          "water_in_connections",
          "water_out_connections",
          "incoming_water_pressure_bar",
          "steam_output_capacity",

          "recommended_steam_generator_size",
          "Construction_materials",
          "optional_features",
          "steam_room_capacity",

          "temperature_adjustment_range_cel_min",
          "temperature_adjustment_range_cel_max",
          "operating_time_adjustment_range_family_sauna_min",
          "operating_time_adjustment_range_family_sauna_max",
          "ambient_temperature_min",
          "ambient_temperature_max",
          "storage_temperature_min",
          "storage_temperature_max",
          "model",
          "infrared_a_wave_amount",
          "infrared_b_wave_amount",
          "infrared_c_wave_amount",
          "infrared_wave_types",
          "infrared_radiator_type",
          "surface_temperature_range_celsius",
          "wavelength_range_nm",

          "door_size",
          "handedness",
          "glass_color",
          "door_knob_type",
          "hinge_type",
          "hinge_color",
          "frame_material",
          "heat_intensity",
          "user_ammount",
          "sauna_floor_plan_solutions",
          "material_outside_surface",
          "material_inside_surface",
          "material_benchs",

          "water_filling_type",
          "control_panel_type",
          "product_group_electric_heaters_www",
          "glass_wall",
          "use_enviroment",
          "safety_device_options",
          "remote_use_options",
          "extra_option_category",
          "wood_burning_stove_category",
          "sauna_interior_category",
          "light_category",
          "features_control_units",
          "features_wood_burning_heaters",
          "features_electric_heaters",
          "features_steam_generators",
          "features_control_units",
          "controlled_devices",
          "prime_energy_fuel",

          // PIM contains incorrect data for these fields so don't show them: https://app.asana.com/1/1201313028356285/task/1210110473639447/comment/1210122343485768
          // "warranty_consumer_months",
          // "warranty_professional_months",
          // "warranty_community_months",
        ]}
      />
      <ProductComparisonAccordion
        title={t("compareDimensions", "Dimensions")}
        products={comparedProducts}
        keys={[
          "product_height",
          "product_width",
          "product_depth",
          "control_panel_width",
          "control_panel_depth",
          "power_unit_height",
          "power_unit_width",
          "power_unit_depth",
          "control_panel_height",
          "stone_sizes",
          "calculatory_sauna_volume",

          "steam_room_width",
          "steam_room_height",
          "steam_room_depth",
          "shower_area_width",
          "shower_area_depth",
          "shower_area_height",
          "overall_tiling_area",
          "Recommended_tiles_size",
        ]}
      />
      <ProductComparisonAccordion
        title={t("compareWeights", "Weights")}
        products={comparedProducts}
        keys={["net_weight", "control_panel_weight", "power_unit_weight", "stone_amount", "water_container_volume"]}
      />

      <ProductComparisonAccordion
        title={t("compareDistances", "Technical distances")}
        products={comparedProducts}
        keys={[
          "safety_distance_front",
          "safety_distance_front_to_the_light_constructions",
          "safety_distance_side",
          "safety_distance_side_to_the_light_constructions",
          "safety_distance_behind",
          "safety_distance_behind_to_the_light_constructions",
          "safety_distance_right_side_combustible_materials",
          "safety_distance_right_side_non_combustible_materials",
          "safety_distance_left_side_combustible_materials",
          "safety_distance_left_side_non_combustible_materials",
          "safety_distance_ceiling",
          "safety_distance_floor_3ph",
          "safety_distance_floor_1ph",
          "min_height_for_assembly",
          "installation_opening_height",
          "installation_opening_width",
          "installation_opening_depth",
          "control_unit_cable_lenght",
          "sensor_cable_lenght",
          "room_min_volume",
          "room_max_volume",
          "room_min_height",
        ]}
      />

      <ProductComparisonAccordion
        title={t("compareElectrical", "Electrical")}
        products={comparedProducts}
        keys={[
          "power_table",
          "ip_class_like_ipx4",
          "electrical_frequency",
          "fuse_400V3N_pcs_x_ampers",
          "fuse_400V2N_pcs_x_ampers",
          "fuse_230V1N_pcs_x_ampers",
          "fuse_230V3_pcs_x_ampers",
          "fuse_240VAC1phs_pcs_x_ampers",
          "fuse_208VAC3phs_pcs_x_ampers",
          "fuses_ambiguous_depends_on_voltage",
          "supply_cable_400V3N_mm2",
          "supply_cable_400V2N_mm2",
          "supply_cable_230V1N_mm2",
          "supply_cable_230V3AC_mm2",
          "supply_cable_240VAC1ph_mm2",
          "supply_cable_208VAC3ph_mm2",
          "fan_lead_230v",
          "max_connection_power_for_light",
          "max_connection_power_for_light_dimmable",
          "max_connection_power_for_fan",
          "max_connection_power_for_fan_dimmable",
          "max_conection_power_for_out1",
          "max_conection_power_for_out2",
          "led_pilot_light_voltage",
          "switch_input_voltage",
          "light_lead_230v",
          "sensor_lead_230v",
          "efficiency",
          "electrical_group_options",
        ]}
      />

      <ProductComparisonAccordion
        title={t("compareLogistics", "Logistics")}
        products={comparedProducts}
        keys={[
          "gtin",
          "electrical_number_stk",
          "customs_tariff_number_eu",
          "customs_tariff_number_ru",
          "etim_number",
          "unspsc_number",
          //"content_of_delivery_localized",
          "content_of_delivery_listed",
          //"content_of_delivery",
          "product_unit_pcs_set_m",
          "unit",
          "quantity",
          "delivery_time_days",
          "packaging_materials",
          "package_height",
          "package_width",
          "package_depth",
          "gross_weight",
          "cardboard_weight",
          "plastic_weight",
          "wood_weight",
          "metal_weight",
          "sales_lot_2_quantity",
          "sales_lot_2_gtin",
          "sales_lot_2_packaging_materials",
          "sales_lot_3_quantity",
          "sales_lot_3_gtin",
          "sales_lot_3_packaging_materials",
          "sales_lot_2_package_height",
          "sales_lot_2_package_width",
          "sales_lot_2_package_depth",
          "sales_lot_3_package_height",
          "sales_lot_3_package_width",
          "sales_lot_3_package_depth",
          "sales_lot_2_gross_weight",
          "sales_lot_2_cardboard_weight",
          "sales_lot_2_plastic_weight",
          "sales_lot_2_wood_weight",
          "sales_lot_2_metal_weight",
          "sales_lot_3_gross_weight",
          "sales_lot_3_cardboard_weight",
          "sales_lot_3_plastic_weight",
          "sales_lot_3_wood_weight",
          "sales_lot_3_metal_weight",
        ]}
      />

      <p>
        <small>
          <i>{t("compareDisclaimer", "")}</i>
        </small>
      </p>
    </>
  )
}

export default ProductComparison
